/**
 * =============================================================================
 * IDS CRM Web - Coordinates copy control (2026-08-12)
 * =============================================================================
 *
 * Shows a lat/lng pair in the one format every satnav app accepts
 * ("54.123456, -6.123456") with a copy-to-clipboard button and an
 * "Open in Maps" link (Google Maps URL — hands off to the installed
 * maps/satnav app on phones).
 *
 * Copy uses navigator.clipboard when available but ALWAYS falls back to a
 * hidden-textarea execCommand copy: the async clipboard API only exists in
 * secure contexts, and the deployed app may be served over plain http on
 * the LAN.
 *
 * =============================================================================
 */

import React, { useState, useRef, useEffect } from 'react';
import './CoordsCopy.css';

export const formatCoords = (lat, lng) => {
  const latNum = Number(lat);
  const lngNum = Number(lng);
  if (!Number.isFinite(latNum) || !Number.isFinite(lngNum)) return null;
  return `${latNum.toFixed(6)}, ${lngNum.toFixed(6)}`;
};

const copyText = async (text) => {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // insecure context / permission denied — fall through
    }
  }
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  document.body.appendChild(textarea);
  textarea.select();
  let ok = false;
  try {
    ok = document.execCommand('copy');
  } catch {
    ok = false;
  }
  document.body.removeChild(textarea);
  return ok;
};

function CoordsCopy({ lat, lng, compact = false }) {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const coords = formatCoords(lat, lng);
  if (!coords) return null;

  const handleCopy = async (event) => {
    // Inside map popups a stray click can close the popup — keep it open.
    event.preventDefault();
    event.stopPropagation();
    const ok = await copyText(coords);
    if (ok) {
      setCopied(true);
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setCopied(false), 1500);
    }
  };

  return (
    <span className={`coords-copy ${compact ? 'compact' : ''}`}>
      {!compact && <span className="coords-value">{coords}</span>}
      <button
        type="button"
        className={`coords-copy-btn ${copied ? 'copied' : ''}`}
        onClick={handleCopy}
        title={`Copy coordinates for satnav (${coords})`}
      >
        {copied ? '✓ Copied' : 'Copy coords'}
      </button>
      <a
        className="coords-maps-link"
        href={`https://www.google.com/maps?q=${coords.replace(' ', '')}`}
        target="_blank"
        rel="noopener noreferrer"
        title="Open in Google Maps / satnav"
        onClick={event => event.stopPropagation()}
      >
        Open in Maps
      </a>
    </span>
  );
}

export default CoordsCopy;
